/* FEATURE SPRINT 2A: shared proximity engine. Given coordinates, ranks nearby
   entities: same locality string → within 2km → same city. Used both
   directions (businesses near a masjid, masjids near a business). */

export type GeoEntity = {
  latitude?: string | number | null
  longitude?: string | number | null
  locality?: string | null
  area?: string | null
  city?: string | null
}

const EARTH_KM = 6371

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(lat2 - lat1)
  const dLon = rad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.sqrt(a))
}

const norm = (v: string | null | undefined) => String(v || '').toLowerCase().trim()
const coord = (v: string | number | null | undefined) => {
  const n = Number(v)
  return Number.isFinite(n) && n !== 0 ? n : null
}

export type Ranked<T> = T & { distanceKm: number | null; proximityTier: 'locality' | 'radius' | 'city' }

/* Rank candidates around an origin. Returns at most `limit`, best first. */
export function rankNearby<T extends GeoEntity>(origin: GeoEntity, candidates: T[], limit = 5, radiusKm = 2): Ranked<T>[] {
  const oLat = coord(origin.latitude)
  const oLon = coord(origin.longitude)
  const oLocality = norm(origin.locality || origin.area)
  const oCity = norm(origin.city)

  const scored: Ranked<T>[] = []
  for (const c of candidates) {
    const cLat = coord(c.latitude)
    const cLon = coord(c.longitude)
    const distanceKm = oLat !== null && oLon !== null && cLat !== null && cLon !== null
      ? haversineKm(oLat, oLon, cLat, cLon)
      : null
    const cLocality = norm(c.locality || c.area)
    const sameLocality = !!oLocality && oLocality === cLocality
    const inRadius = distanceKm !== null && distanceKm <= radiusKm
    const sameCity = !!oCity && oCity === norm(c.city)
    if (!sameLocality && !inRadius && !sameCity) continue
    scored.push({
      ...c,
      distanceKm: distanceKm !== null ? Math.round(distanceKm * 10) / 10 : null,
      proximityTier: sameLocality ? 'locality' : inRadius ? 'radius' : 'city',
    })
  }
  const tierRank = { locality: 0, radius: 1, city: 2 } as const
  scored.sort((a, b) =>
    tierRank[a.proximityTier] - tierRank[b.proximityTier] ||
    (a.distanceKm ?? 99) - (b.distanceKm ?? 99),
  )
  return scored.slice(0, limit)
}
